import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Zap, Shield, Globe, ArrowRight } from 'lucide-react';

export const Hero = () => {
  const { t } = useTranslation();
  const bgVideoRef = useRef(null);
  const fgVideoRef = useRef(null);

  useEffect(() => {
    const playVideos = () => {
      if (bgVideoRef.current) bgVideoRef.current.play().catch(() => {});
      if (fgVideoRef.current) fgVideoRef.current.play().catch(() => {});
    };
    playVideos();
    const handleInteraction = () => {
      playVideos();
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
    };
    document.addEventListener('touchstart', handleInteraction, { once: true });
    document.addEventListener('click', handleInteraction, { once: true });
    return () => {
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('click', handleInteraction);
    };
  }, []);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden flex items-center bg-black">
      <div className="absolute inset-0 z-0">
        <video ref={bgVideoRef} data-testid="hero-bg-video" autoPlay loop muted playsInline preload="auto" src={`${process.env.PUBLIC_URL}/videos/hero-earth-bg.mp4`} className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 1 }} />
        <div className="absolute inset-0 bg-black/40" style={{ zIndex: 2 }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" style={{ zIndex: 3 }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[hsl(var(--background))]" style={{ zIndex: 4 }} />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-20 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-5 sm:mb-6">
              <Badge data-testid="badge-api-first" variant="outline" className="border-[hsl(var(--primary-glow)/0.4)] bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary-glow))] backdrop-blur-sm text-[10px] sm:text-xs">
                <Zap className="w-3 h-3 mr-1" /> API First
              </Badge>
              <Badge data-testid="badge-testnet" variant="outline" className="border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] text-[hsl(var(--success))] backdrop-blur-sm text-[10px] sm:text-xs">
                <Shield className="w-3 h-3 mr-1" /> {t('hero.badge_testnet')}
              </Badge>
              <Badge data-testid="badge-uptime" variant="outline" className="border-[hsl(var(--neon-accent)/0.4)] bg-[hsl(var(--neon-accent)/0.1)] text-[hsl(var(--neon-accent))] backdrop-blur-sm text-[10px] sm:text-xs">
                <Globe className="w-3 h-3 mr-1" /> 99.99% Uptime
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[2.7rem] xl:text-5xl font-bold tracking-tight text-gradient-hero leading-[1.1]">
              {t('hero.title_line1')}<br />{t('hero.title_line2')}<br />{t('hero.title_line3')}<br />{t('hero.title_line4')}
            </h1>
            <p className="mt-3 sm:mt-4 text-base sm:text-xl lg:text-2xl font-medium text-foreground/70">{t('hero.subtitle')}</p>
            <p className="mt-4 sm:mt-6 text-sm sm:text-base text-foreground/80 leading-relaxed max-w-xl">{t('hero.description')}</p>
            <div className="mt-6 sm:mt-8 flex flex-wrap gap-3 sm:gap-4">
              <Button data-testid="hero-primary-cta" size="lg" className="btn-neon text-sm sm:text-base px-6 sm:px-8 h-11 sm:h-12 gap-2 group">
                {t('hero.cta_primary')}
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Button>
              <Button data-testid="hero-secondary-cta" variant="outline" size="lg" className="border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))] hover:border-[hsl(var(--primary-glow)/0.4)] text-sm sm:text-base px-6 sm:px-8 h-11 sm:h-12 transition-all duration-300">
                {t('hero.cta_secondary')}
              </Button>
            </div>
            <div className="mt-8 sm:mt-12 flex items-center gap-5 sm:gap-8 lg:gap-12">
              <div>
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">$2.4M+</div>
                <div className="text-[10px] sm:text-xs text-foreground/60 mt-0.5">{t('hero.stat_volume')}</div>
              </div>
              <div className="w-px h-8 sm:h-10 bg-[hsl(var(--border))]" />
              <div>
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">15K+</div>
                <div className="text-[10px] sm:text-xs text-foreground/60 mt-0.5">{t('hero.stat_transactions')}</div>
              </div>
              <div className="w-px h-8 sm:h-10 bg-[hsl(var(--border))]" />
              <div>
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-[hsl(var(--success))]">99.7%</div>
                <div className="text-[10px] sm:text-xs text-foreground/60 mt-0.5">{t('hero.stat_success')}</div>
              </div>
            </div>
          </div>
          <div className="relative flex items-center justify-center mt-4 lg:mt-0">
            <div className="absolute -inset-4 sm:-inset-6 rounded-3xl bg-gradient-to-br from-[hsl(var(--primary)/0.12)] via-transparent to-[hsl(var(--neon-accent)/0.08)] blur-2xl opacity-70 animate-glow-pulse" />
            <div className="relative rounded-2xl overflow-hidden neon-border shadow-[0_0_50px_hsl(var(--primary)/0.25)]">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary-glow)/0.7)] to-transparent z-10" />
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--neon-accent)/0.5)] to-transparent z-10" />
              <div className="absolute top-0 bottom-0 left-0 w-px bg-gradient-to-b from-transparent via-[hsl(var(--primary-glow)/0.4)] to-transparent z-10" />
              <div className="absolute top-0 bottom-0 right-0 w-px bg-gradient-to-b from-transparent via-[hsl(var(--neon-accent)/0.3)] to-transparent z-10" />
              <div className="absolute inset-0 bg-[hsl(var(--surface))] flex items-center justify-center z-0">
                <div className="w-8 h-8 border-2 border-[hsl(var(--primary-glow)/0.3)] border-t-[hsl(var(--primary-glow))] rounded-full animate-spin" />
              </div>
              <video ref={fgVideoRef} data-testid="hero-video" autoPlay loop muted playsInline preload="auto" src={`${process.env.PUBLIC_URL}/videos/hero-fintech.mp4`} className="relative z-[1] w-full max-w-[540px] aspect-video object-cover rounded-2xl" />
              <div className="absolute inset-0 z-[2] pointer-events-none rounded-2xl bg-gradient-to-t from-[hsl(var(--background)/0.15)] via-transparent to-transparent" />
            </div>
            <div className="absolute -top-3 -right-2 sm:-top-5 sm:-right-5 glass rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1.5 sm:gap-2 animate-float z-20 shadow-[0_0_15px_hsl(var(--warning)/0.15)]" style={{ animationDelay: '0.5s' }}>
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[hsl(var(--warning)/0.2)] flex items-center justify-center text-[8px] sm:text-[10px] font-bold text-[hsl(var(--warning))]">BTC</div>
              <span className="text-[10px] sm:text-xs font-mono text-[hsl(var(--success))]">+$1,250</span>
            </div>
            <div className="absolute -bottom-3 -left-2 sm:-bottom-5 sm:-left-5 glass rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1.5 sm:gap-2 animate-float z-20 shadow-[0_0_15px_hsl(var(--success)/0.15)]" style={{ animationDelay: '1.5s' }}>
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[hsl(var(--success)/0.2)] flex items-center justify-center text-[8px] sm:text-[10px] font-bold text-[hsl(var(--success))]">USDT</div>
              <span className="text-[10px] sm:text-xs font-mono text-[hsl(var(--success))]">+$3,400</span>
            </div>
            <div className="absolute top-1/2 -right-3 sm:-right-8 -translate-y-1/2 glass rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1.5 sm:gap-2 animate-float z-20 shadow-[0_0_15px_hsl(var(--primary-glow)/0.15)]" style={{ animationDelay: '2.5s' }}>
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[hsl(var(--primary-glow)/0.2)] flex items-center justify-center text-[8px] sm:text-[10px] font-bold text-[hsl(var(--primary-glow))]">ETH</div>
              <span className="text-[10px] sm:text-xs font-mono text-[hsl(var(--success))]">+$890</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
